"use client";

import { createMarkdownComponents } from "./markdownComponents";
import { cn } from "../lib/utils";
import type {
  LegacyThreadMessage,
  LegacyContent as Content,
  LegacyToolUseContent,
  LegacyComponentContent,
} from "../lib/cartAssistant/types";
import stringify from "json-stringify-pretty-compact";
import { Check, ChevronDown, Loader2, Copy } from "lucide-react";
import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { useTranslation } from 'react-i18next';
import { components as registeredComponents } from "../lib/cartAssistant/registry";
import { SmartImage } from "../../components/common/SmartImage";
import { validateChatMessage } from "../../services/geminiService";

interface MessageContentProps {
  message: LegacyThreadMessage;
  isLoading?: boolean;
  role: "user" | "assistant" | "system";
  className?: string;
  onPreviewImage?: (uri: string) => void;
}

/**
 * Async text validation cache to prevent redundant LLM calls
 */
const validationCache = new Map<string, boolean>();

/**
 * Component to handle async validation of AI messages
 */
const ValidatedText = ({
  text,
  role,
  onPreviewImage,
  messageId
}: {
  text: string,
  role: string,
  onPreviewImage?: (uri: string) => void,
  messageId: string
}) => {
  const { t } = useTranslation();
  const markdownComponents = React.useMemo(
    () => createMarkdownComponents(onPreviewImage, t),
    [onPreviewImage, t]
  );
  const [isValid, setIsValid] = useState<boolean | null>(() => {
    // Immediate check for non-assistant roles or cached results
    if (role !== "assistant") return true;
    
    // Stable check: If this message ID was already validated, trust it
    if (validationCache.has(messageId)) return validationCache.get(messageId)!;
    
    return null;
  });

  React.useEffect(() => {
    // Skip if already validated or not an assistant message
    if (isValid !== null || role !== "assistant") return;
    
    const trimmed = text.trim();
    if (!trimmed) {
      setIsValid(true);
      return;
    }

    // HEURISTIC: If text is long enough and contains standard structure, it's valid.
    // This catches large streaming updates that are clearly prose.
    if (trimmed.length > 100) {
      setIsValid(true);
      validationCache.set(messageId, true);
      return;
    }

    let mounted = true;
    const checkText = async () => {
      // Small delay to allow streaming buffer to accumulate
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!mounted) return;

      const result = await validateChatMessage(text);
      if (mounted) {
        validationCache.set(messageId, result);
        setIsValid(result);
      }
    };

    checkText();
    return () => { mounted = false; };
  }, [role, isValid, messageId]);

  if (isValid === false) return null;
  
  // Show nothing while validating assistant text to prevent "jerkiness"
  if (isValid === null && role === "assistant") return null;

  return (
    <div className={cn("relative group/bubble w-fit", role === "user" ? "ml-auto max-w-[85%]" : "mr-auto max-w-[90%]")}>
      <div className={cn(
        "text-[14px] leading-relaxed whitespace-pre-wrap break-words px-4 py-3 animate-in fade-in duration-300",
        role === "user"
          ? "bg-emerald-600 text-white rounded-[1.5rem] rounded-br-md shadow-sm shadow-emerald-600/20"
          : "bg-transparent text-[#1a2b3c] rounded-[1.5rem] rounded-bl-md"
      )}>
        <ReactMarkdown components={markdownComponents}>
          {text}
        </ReactMarkdown>
      </div>

    </div>
  );
};

/**
 * Strip [img:URL] markers from text for clean display
 */
const stripMarkers = (text: string): string => {
  if (!text) return text;
  return text.replace(/\[img:([^\]]+)\]/g, '').trim();
};

/**
 * Scrub leaked internal logic/tool-calling syntax from AI text
 * Handles patterns like "+#+#+#+#+#+", "to=functions", and stray JSON objects
 */
const scrubLeakedLogic = (text: string): string => {
  if (!text) return text;
  
  return text
    // Remove "to=functions.xxx" or "functions.xxx" leaks
    .replace(/(?:to=)?functions\.[a-zA-Z0-9_]+/g, '')
    // Remove "+#+#+#+" or any repeating sequence of + and # leaks
    .replace(/[+#]{4,}.*?(?=\n|$)/g, '')
    // Remove Chinese/Japanese punctuation artifacts like 】【。】【”】【
    .replace(/[】{}[\]。，”"']{3,}/g, '')
    // Remove stray code commentary leaks
    .replace(/code in commentaries(?:->json)?/gi, '')
    // Remove raw JSON objects (often leaked tool input)
    .replace(/\{"name":.*?"imageUrl":.*?\}/g, '')
    // Remove leaked "to=" target syntax
    .replace(/to=[a-zA-Z0-9_.]+/g, '')
    // Remove generic "I will now call..." internal thoughts if they leak
    .replace(/I will now call.*?(?:\.|$)/gi, '')
    // Clean up trailing/leading junk after replacements
    .replace(/^\s*[\-\.]+\s*$/, '')
    .trim();
};

export const MessageActions = ({ message, children }: { message: LegacyThreadMessage; children?: React.ReactNode }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => () => { if (timeout.current) clearTimeout(timeout.current); }, []);
  const raw = typeof message.content === 'string' ? message.content
    : message.content.filter(block => block.type === 'text').map(block => (block as { text: string }).text).join('\n\n');
  const text = scrubLeakedLogic(stripMarkers(raw));
  if (message.role === 'system') return null;
  const canCopy = message.role === 'assistant' && !!text.trim();
  const copyLabel = copied ? t('common.copied', { defaultValue: 'Copied' }) : t('chat.copy', { defaultValue: 'Copy' });
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = setTimeout(() => setCopied(false), 2000);
    } catch { setCopied(false); }
  };
  return <div className={cn('mt-1 flex min-h-8 items-center gap-2 px-2', message.role === 'user' ? 'justify-end' : 'justify-start')}>
    {canCopy && <button type="button" onClick={handleCopy} aria-label={copyLabel} title={copyLabel} className="flex size-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
      {copied ? <Check size={15} strokeWidth={2} className="text-emerald-500" aria-hidden="true" /> : <Copy size={15} strokeWidth={1.75} aria-hidden="true" />}
    </button>}
    {children}
  </div>;
};

export const MessageContent = ({ message, isLoading, role, className, onPreviewImage }: MessageContentProps) => {
  const { t } = useTranslation();

  // SAFETY: Never render system messages or prompts in the UI
  if (role === 'system') return null;
  
  const rawText = typeof message.content === 'string' 
    ? message.content 
    : (Array.isArray(message.content) ? message.content.map(c => (c as any).text || '').join(' ') : '');
    
  if (
    rawText.includes('You are a CartLens Sidekick') || 
    rawText.includes('VISUAL INTELLIGENCE MANDATORY') ||
    rawText.includes('PRODUCT SIGNATURE RULE')
  ) return null;

  // Helper to extract all image URLs from [img:URL] markers
  const extractMarkers = (text: string): string[] => {
    const regex = /\[img:([^\]]+)\]/g;
    const urls: string[] = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      urls.push(match[1]);
    }
    return urls;
  };

  // Support legacy string content
  if (typeof message.content === 'string') {
    const textContent = message.content as string;
    if (textContent.trim() === "" || textContent.includes('[TERMINAL_USER_APPROVAL]')) return null;
    
    const markers = extractMarkers(textContent);
    const cleanText = scrubLeakedLogic(stripMarkers(textContent));

    if (!cleanText && markers.length === 0) return null;

    return (
      <div className={cn("flex flex-col gap-2 w-full", className)}>
        {cleanText && (
          <ValidatedText 
            text={cleanText} 
            role={role} 
            messageId={message.id} 
            onPreviewImage={onPreviewImage} 
          />
        )}
        {markers.map((url, i) => (
          <div 
            key={`img-legacy-${i}`}
            className={cn("rounded-2xl shadow-md max-w-[240px] cursor-pointer overflow-hidden border border-gray-100 mt-[-8px] relative z-10 hover:shadow-lg transition-shadow", role === "user" ? "ml-auto" : "mr-auto")}
            onClick={() => onPreviewImage?.(url)}
          >
            <SmartImage imageUrl={url} alt={t('chat.attached')} className="w-full h-auto" />
          </div>
        ))}
      </div>
    );
  }

  const contentArray = Array.isArray(message.content) ? message.content : [];
  if (contentArray.length === 0) return null;

  // Separate non-tool blocks for rendering
  const renderableBlocks = contentArray.filter(block => {
    if (block.type === 'tool_use' || block.type === 'tool_result') return false;
    if (block.type === 'text') {
      const scrubbed = scrubLeakedLogic(block.text || "");
      return scrubbed && scrubbed.trim() !== "" && !scrubbed.includes('[TERMINAL_USER_APPROVAL]');
    }
    return true;
  });

  // Helper to normalize URL for comparison (removes query params)
  const normalizeUrl = (url: string): string => {
    if (!url) return "";
    try {
      // Handle base64 blobs - they are unique by themselves
      if (url.startsWith('data:')) return url.substring(0, 100); 
      return url.split('?')[0].split('#')[0].trim();
    } catch (e) {
      return url;
    }
  };

  // COLLECT UNIQUE IMAGES: Combine markers and resource blocks
  const allMarkers = renderableBlocks
    .filter(b => b.type === 'text')
    .flatMap(b => extractMarkers((b as any).text || ""));

  const resourceUrls = renderableBlocks
    .filter(b => b.type === 'resource')
    .map(b => {
      const res = (b as any).resource;
      if (!res) return null;
      return res.uri || res.url || (res.blob ? (res.blob.startsWith('http') ? res.blob : `data:${res.mimeType || 'image/jpeg'};base64,${res.blob}`) : null);
    })
    .filter(Boolean) as string[];

  // INTELLIGENT DEDUPLICATION: 
  // If we have manual markers in the text, we trust those (they are usually local high-fidelity blobs).
  // We only show resource blocks if no markers were found in the text for this message.
  const uniqueImages = allMarkers.length > 0 ? allMarkers : resourceUrls;

  // Separate blocks by type for strict ordering
  const textBlocks = renderableBlocks.filter(b => b.type === 'text');
  const componentBlocks = renderableBlocks.filter(b => b.type === 'component');
  const filteredBlocks = renderableBlocks.filter(b => b.type !== 'resource' && b.type !== 'text' && b.type !== 'component');

  const hasComponents = componentBlocks.length > 0;
  if (textBlocks.length === 0 && uniqueImages.length === 0 && !hasComponents) return null;

  const toolBlocks = contentArray.filter(b => b.type === 'tool_use') as LegacyToolUseContent[];

  return (
    <div className={cn("flex flex-col gap-2 w-full", className)}>
      {/* 0. WHAT THE ASSISTANT DID — one quiet line above the answer */}
      {toolBlocks.length > 0 && <ToolStepsLine blocks={toolBlocks} isThreadIdle={!isLoading} messageId={message.id} />}

      {/* 1. TEXT BLOCKS */}
      {textBlocks.map((block, index) => {
        const scrubbedText = scrubLeakedLogic(stripMarkers((block as any).text || ""));
        if (!scrubbedText) return null;

        return (
          <ValidatedText 
            key={`${message.id}-text-${index}`} 
            text={scrubbedText} 
            role={role} 
            messageId={message.id} 
            onPreviewImage={onPreviewImage} 
          />
        );
      })}

      {/* 2. UNIQUE IMAGES SECOND */}
      {uniqueImages.map((url, i) => (
        <div 
          key={`img-block-${i}`}
          className={cn("rounded-2xl shadow-md max-w-[240px] cursor-pointer overflow-hidden border border-gray-100 mt-[-8px] relative z-10 hover:shadow-lg transition-shadow", role === "user" ? "ml-auto" : "mr-auto")}
          onClick={() => onPreviewImage?.(url)}
        >
          <SmartImage imageUrl={url} alt={t('chat.attached')} className="w-full h-auto" />
        </div>
      ))}

      {/* 3. COMPONENT BLOCKS (CARDS) LAST (Delayed if text is present) */}
      {componentBlocks.map((block, index) => {
        const nonce = ((block as any).props)?.nonce;
        const key = `${message.id}-${index}-comp-${(block as LegacyComponentContent).name}${nonce ? `-${nonce}` : ''}`;
        
        // Apply significant delay if there is text to read first (especially for assistant)
        // This creates a "posted text, then card follows" rhythmic flow
        const shouldDelay = textBlocks.length > 0 && role === "assistant";

        return (
          <div 
            key={key} 
            className={cn(
              "w-full my-1 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both",
              shouldDelay ? "delay-300" : "delay-100"
            )}
          >
            <ComponentBlock block={block as LegacyComponentContent} />
          </div>
        );
      })}
      
      {/* Other blocks if any */}
      {filteredBlocks.map((block, index) => (
        <ContentBlock key={`${message.id}-other-${index}`} block={block} isIdle={!isLoading} role={role} messageId={message.id} onPreview={onPreviewImage} />
      ))}

    </div>
  );
};

const ContentBlock = ({ block, isIdle, role, messageId, onPreview }: { block: Content, isIdle: boolean, role: string, messageId: string, onPreview?: (uri: string) => void; }) => {
  const { t } = useTranslation();
  switch (block.type) {
    case "text":
      if (!block.text || block.text.trim() === "" || block.text.includes('[TERMINAL_USER_APPROVAL]') || block.text.includes('[TERMINAL_USER_CANCELLED]')) return null;
      return (
        <ValidatedText 
          text={stripMarkers(block.text)} 
          role={role} 
          messageId={messageId} 
          onPreviewImage={onPreview} 
        />
      );
    case "component":
      return (
        <div className="w-full my-1 animate-in zoom-in-95 duration-500">
          <ComponentBlock block={block as LegacyComponentContent} onPreview={onPreview} />
        </div>
      );
    default:
      return null;
  }
};

/**
 * Compact "what I did" line: a check (or spinner) plus the tool steps in plain
 * language, collapsed into one row. Expanding shows each step's input payload.
 */
const ToolPayloadStep = ({ block, isThreadIdle }: { block: LegacyToolUseContent; isThreadIdle: boolean }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const id = React.useId();
  const done = isThreadIdle || block.hasCompleted;
  return <div className="min-w-0 py-1">
    <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls={id} className="flex w-full min-w-0 items-start gap-2 rounded-lg py-2 text-left text-gray-500 transition-colors hover:text-[#1a2b3c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
      {done ? <Check size={13} className="mt-0.5 shrink-0 text-emerald-500" aria-hidden="true" /> : <Loader2 size={13} className="mt-0.5 shrink-0 animate-spin text-emerald-500" aria-hidden="true" />}
      <span className="min-w-0 flex-1 break-words text-xs leading-relaxed">{block.statusMessage || block.name.replace(/_/g, ' ')}</span>
      <ChevronDown size={13} className={cn('mt-0.5 shrink-0 text-gray-400 transition-transform', open && 'rotate-180')} aria-hidden="true" />
    </button>
    <AnimatePresence initial={false}>
      {open && <motion.div id={id} initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }} className="overflow-hidden">
        <pre tabIndex={0} aria-label={t('chat.payload_data', { defaultValue: 'Payload Data' })} className="my-1 ml-5 max-h-56 max-w-full overflow-auto overscroll-contain rounded-lg py-2 text-[11px] font-mono leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-gray-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500">
          {stringify(block.input ?? {}, { maxLength: 60 })}
        </pre>
      </motion.div>}
    </AnimatePresence>
  </div>;
};

const ToolStepsLine = ({ blocks, isThreadIdle, messageId }: { blocks: LegacyToolUseContent[]; isThreadIdle: boolean; messageId: string }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const panelId = React.useId();
  const allDone = isThreadIdle || blocks.every(block => block.hasCompleted);
  return <div className="w-full min-w-0 px-3 py-1">
    <button type="button" onClick={() => setIsExpanded(value => !value)} aria-expanded={isExpanded} aria-controls={panelId} className="inline-flex max-w-full items-center gap-2 rounded-lg py-2 text-xs font-medium text-gray-400 transition-colors hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
      {allDone ? <Check size={13} className="text-emerald-500" aria-hidden="true" /> : <Loader2 size={13} className="animate-spin text-emerald-500" aria-hidden="true" />}
      <span>{t('chat.actions', { defaultValue: 'Actions' })}</span>
      <span className="font-mono text-[10px] opacity-70">{blocks.length}</span>
      <ChevronDown size={13} className={cn('transition-transform duration-200', isExpanded && 'rotate-180')} aria-hidden="true" />
    </button>
    <AnimatePresence initial={false}>
      {isExpanded && <motion.div id={panelId} initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
        <div className="my-1 ml-1.5 max-h-80 space-y-1 overflow-y-auto overscroll-contain border-l border-gray-200 pl-4">
          {blocks.map((block, index) => <ToolPayloadStep key={`${messageId}-step-${index}`} block={block} isThreadIdle={isThreadIdle} />)}
        </div>
      </motion.div>}
    </AnimatePresence>
  </div>;
};

const ComponentBlock = ({ block, onPreview }: { block: LegacyComponentContent, onPreview?: (url: string) => void }) => {
  const { t } = useTranslation();
  
  // Try to use the SDK's rendered component
  if (block && block.renderedComponent) {
    try {
      return (
        <div className="w-full my-2">
          {block.renderedComponent}
        </div>
      );
    } catch (e) {
      console.warn("[CartAssistant] Failed to render SDK component, falling back:", e);
    }
  }

  // FALLBACK: If SDK failed to render, but we have data, render manually
  if (block && block.name) {
    const registryEntry = registeredComponents.find(c => c.name === block.name);
    if (registryEntry) {
      const Component = registryEntry.component;
      const streaming = block.streamingState === 'streaming' || block.streamingState === 'started';
      return (
        <div className="w-full my-2">
          <Component props={{ ...(block.props as any), onPreviewImage: onPreview }} isStreaming={streaming} />
        </div>
      );
    }
  }

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground p-4 border border-dashed rounded-2xl">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span>{t('chat.preparing_ui')}</span>
    </div>
  );
};

export const LoadingIndicator: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => {
  return (
    <div className={cn("flex items-center gap-1", className)} {...props}>
      <span className="w-1 h-1 bg-current rounded-full animate-bounce [animation-delay:-0.3s]"></span>
      <span className="w-1 h-1 bg-current rounded-full animate-bounce [animation-delay:-0.2s]"></span>
      <span className="w-1 h-1 bg-current rounded-full animate-bounce [animation-delay:-0.1s]"></span>
    </div>
  );
};
